import React, { useState, useEffect } from "react";
import { Youtube, ExternalLink } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

// Helper to extract YouTube ID
function getYouTubeId(url) {
  if (!url) return null;
  if (url.includes("youtu.be/")) return url.split("youtu.be/")[1].split("?")[0];
  if (url.includes("v=")) return url.split("v=")[1].split("&")[0];
  if (url.includes("embed/")) return url.split("embed/")[1].split("?")[0];
  return null;
}

// Placeholder videos when none are in DB
const PLACEHOLDER_VIDEOS = [
  { id: "p1", title: "How Credit Scores Work in Canada — Explained Simply", description: "Everything newcomers and families need to know about building credit from zero.", youtube_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", topic: "Credit Building" },
  { id: "p2", title: "Renting vs Buying in 2025: The Math", description: "We run the actual numbers so you don't have to guess.", youtube_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", topic: "Mortgages" },
  { id: "p3", title: "TFSA vs RRSP: Which One First?", description: "A plain-English breakdown of which account fits your situation as a newcomer.", youtube_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", topic: "Newcomer Essentials" },
  { id: "p4", title: "The True Cost of Living in Toronto", description: "Honest monthly numbers — rent, groceries, transit, and the hidden costs.", youtube_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ", topic: "Cost of Living" },
];

function VideoCard({ video }) {
  const ytId = getYouTubeId(video.youtube_url);
  const thumbnailUrl = ytId
    ? `https://img.youtube.com/vi/${ytId}/hqdefault.jpg`
    : "https://images.pexels.com/photos/4319926/pexels-photo-4319926.jpeg";

  return (
    <div
      className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden hover:shadow-md hover:-translate-y-1 transition-all duration-300 group"
      data-testid={`video-card-${video.id}`}
    >
      <div className="relative">
        <img
          src={thumbnailUrl}
          alt={video.title}
          className="w-full h-48 object-cover"
          onError={(e) => { e.target.src = "https://images.pexels.com/photos/4319926/pexels-photo-4319926.jpeg"; }}
        />
        <div className="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <a
            href={video.youtube_url}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-red-600 text-white rounded-full p-3 hover:bg-red-700 transition-colors"
            aria-label="Watch on YouTube"
          >
            <Youtube size={20} />
          </a>
        </div>
        {video.topic && (
          <div className="absolute top-3 left-3">
            <span className="bg-[#1B4332]/90 text-white text-xs font-semibold px-2 py-1 rounded">
              {video.topic}
            </span>
          </div>
        )}
      </div>
      <div className="p-5 space-y-2">
        <h3 className="font-bold text-[#1C1C1C] text-sm leading-snug group-hover:text-[#1B4332] transition-colors">
          {video.title}
        </h3>
        {video.description && (
          <p className="text-[#57534E] text-xs leading-relaxed line-clamp-2">{video.description}</p>
        )}
        <a
          href={video.youtube_url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-[#1B4332] text-xs font-semibold mt-2 hover:underline"
        >
          Watch on YouTube <ExternalLink size={11} />
        </a>
      </div>
    </div>
  );
}

export default function Videos() {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios
      .get(`${API}/videos`)
      .then((r) => { setVideos(r.data.length ? r.data : PLACEHOLDER_VIDEOS); setLoading(false); })
      .catch(() => { setVideos(PLACEHOLDER_VIDEOS); setLoading(false); });
  }, []);

  return (
    <div className="min-h-screen bg-[#F9F8F6]">
      {/* Header */}
      <section className="py-16 px-6 border-b border-[#E7E5E4] bg-white">
        <div className="max-w-screen-xl mx-auto grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-4">
            <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373]">Videos</p>
            <h1 className="font-display text-4xl sm:text-5xl font-bold text-[#1C1C1C] tracking-tight">
              Straight talk. On screen.
            </h1>
            <p className="text-[#57534E] text-lg leading-relaxed max-w-lg">
              Daily videos breaking down Canadian money decisions — budgeting, credit, mortgages, and cost of living. No fluff, no sales pitch.
            </p>
            <a
              href="https://youtube.com"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="youtube-channel-link"
              className="inline-flex items-center gap-2 bg-red-600 text-white font-semibold px-5 py-2.5 rounded-md hover:bg-red-700 transition-colors"
            >
              <Youtube size={16} />
              Subscribe on YouTube
            </a>
          </div>
          <div className="rounded-xl overflow-hidden">
            <img
              src="https://images.pexels.com/photos/4319926/pexels-photo-4319926.jpeg"
              alt="Studio microphone representing honest financial conversations"
              className="w-full h-64 object-cover rounded-xl"
            />
          </div>
        </div>
      </section>

      {/* Social media channels */}
      <section className="py-8 px-6 border-b border-[#E7E5E4] bg-[#F2EFE9]">
        <div className="max-w-screen-xl mx-auto">
          <p className="text-xs tracking-[0.2em] uppercase font-semibold text-[#D4A373] mb-4">Also on</p>
          <div className="flex flex-wrap gap-3">
            {[
              { label: "Instagram", href: "https://instagram.com", color: "bg-pink-50 text-pink-700 border-pink-200" },
              { label: "TikTok", href: "https://tiktok.com", color: "bg-gray-50 text-gray-700 border-gray-200" },
              { label: "X / Twitter", href: "https://x.com", color: "bg-blue-50 text-blue-700 border-blue-200" },
              { label: "LinkedIn", href: "https://linkedin.com", color: "bg-blue-50 text-blue-800 border-blue-200" },
              { label: "Substack", href: "https://substack.com", color: "bg-orange-50 text-orange-700 border-orange-200" },
            ].map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                data-testid={`social-channel-${s.label.toLowerCase().replace(/\s/g, "-")}`}
                className={`px-4 py-2 rounded-full border text-sm font-semibold ${s.color} hover:opacity-80 transition-opacity`}
              >
                {s.label}
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Videos grid */}
      <section className="py-12 px-6">
        <div className="max-w-screen-xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="bg-white border border-[#E7E5E4] rounded-xl overflow-hidden animate-pulse">
                  <div className="h-48 bg-[#F2EFE9]" />
                  <div className="p-5 space-y-2">
                    <div className="h-4 bg-[#F2EFE9] rounded w-3/4" />
                    <div className="h-3 bg-[#F2EFE9] rounded w-full" />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {videos.map((v) => <VideoCard key={v.id} video={v} />)}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
